import {
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { BOARD_CONFIG } from "@/config/browserBoard";
import { invalidateCatalogQueries } from "@/entities/thread";
import { usePostedHistoryStore } from "@/features/history/stores/postedHistoryStore";
import { invalidateOrderedMultiAttachmentCapability } from "@/features/post/lib/orderedMultiAttachmentCapability";
import { notifySubmitError } from "@/features/post/lib/submitPostErrorNotification";
import type {
  AltchaCredentialProvider,
  SubmitPostFlowDependencies,
} from "@/features/post/lib/submitPostFlow";
import {
  type SubmitPostInput,
  submitPostFlow,
} from "@/features/post/lib/submitPostFlow";
import { recordSuccessfulPost } from "@/features/post/lib/submitPostSuccess";
import type { CreatePostResult } from "@/shared/api";
import { apiPost, refreshAimgToken, uploadAttachment } from "@/shared/api";
import { toast } from "@/shared/ui/toast";

type SubmitParams = SubmitPostInput & {
  altcha: AltchaCredentialProvider;
};

const SUBMIT_DEPENDENCIES: SubmitPostFlowDependencies = {
  uploadAttachment,
  post: (
    path: string,
    body: object,
    options: { token: string; signal?: AbortSignal; boardKey?: string },
  ): Promise<CreatePostResult> => apiPost(path, body, options),
  refreshToken: refreshAimgToken,
  invalidateMultiAttachmentCapability:
    invalidateOrderedMultiAttachmentCapability,
};

export function useSubmitPost(): UseMutationResult<
  CreatePostResult,
  unknown,
  SubmitParams
> {
  const queryClient = useQueryClient();
  const addPosted = usePostedHistoryStore((state) => state.addPosted);

  return useMutation({
    mutationFn: async ({ altcha, ...input }: SubmitParams) => {
      const { browserPostAttempts } = await import(
        "@/features/post/lib/postAttemptStore"
      );
      return submitPostFlow(
        { ...input, boardKey: input.boardKey ?? BOARD_CONFIG.boardKey },
        altcha,
        { ...SUBMIT_DEPENDENCIES, attempts: browserPostAttempts },
      );
    },
    onSuccess: (
      result: CreatePostResult,
      { boardKey, mode }: SubmitParams,
    ): void => {
      recordSuccessfulPost(
        addPosted,
        boardKey ?? BOARD_CONFIG.boardKey,
        result.threadId,
      );
      if (mode === "thread") {
        void invalidateCatalogQueries(queryClient);
      } else {
        void queryClient.invalidateQueries({ queryKey: ["thread"] });
      }
    },
    onError: (error: unknown): void => {
      notifySubmitError(error, (message) => toast.error(message));
    },
  });
}
